import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Search, Upload, XSquare } from "react-feather";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";
import { Select } from "semantic-ui-react";
import {
    addBlackList,
    getBlackListById,
    searchBlackList,
    updateBlackList,
} from "../../api/blackListApi";
import {
    addAndUpload,
    deleteDocumentList,
    updateAndUpload,
} from "../../api/documentUpload";
import {
    getBulkDropdownData,
    getOtherRefSearchDropdown,
} from "../../api/keyvalue";
import DocumentUpload from "../../components/DocumentUpload";
import Grid from "../../components/Grid";
import LoadingSpinner from "../../components/LoadingSpinner";
import Tab from "../../components/Tab";
import UIMessage from "../../components/UIMessage";
import Button from "../../components/input/Button";
import Dropdown from "../../components/input/Dropdown";
import Input from "../../components/input/Input";
import SearchDropdown from "../../components/input/SearchDropdown";
import SideDrawer from "../../components/input/SideDrawer";
import BlackListForm from "../../components/master/blackList/BlackListForm";
import {
    DateOnlyCellRenderer,
    ResizableScrollingTextRenderer,
} from "../../components/renderer/StyleRenderer";
import { blackListValidation } from "../../components/validations/BlackListCreation";
import { FORMS, MODULES } from "../../const";
import useFirstAndLastAuthorizedTab from "../../hooks/useFirstAndLastAuthorizedTab";
import useNextAndPreviousAuthority from "../../hooks/useNextAndPreviousAuthority";
import { errorActions } from "../../store/error-slice";

const ACTION_KEYS = {
    TEMPORARY_BLOCKED: 1,
    UNBLOCKED: 2,
    PERMANENTLY_BLOCKED: 3,
};

const BlackList = () => {
    const { firstViableTab, lastViableTab } = useFirstAndLastAuthorizedTab({
        module: MODULES.Master,
        section: FORMS.BLACKLIST,
    });

    const errorState = useSelector((state) => state.error.blackListErrors);
    const user = useSelector((state) => state.user);
    const dispatch = useDispatch();

    const [tab, setTab] = useState(1);
    const [drawerTab, setDrawerTab] = useState(firstViableTab);
    const { next, prev } = useNextAndPreviousAuthority({
        sectionKey: "blacklist",
        minTab: 1,
        maxTab: 2,
        module: MODULES.Master,
        section: FORMS.BLACKLIST,
        currentTab: drawerTab,
        authenticated: true,
    });

    const [blkCategory, setBlkCategory] = useState(1);
    const [blkType, setBlkType] = useState(null);
    const [status, setStatus] = useState(null);
    const [active, setActive] = useState(null);
    const [vehEmp, setvehEmp] = useState("");

    const [isLoading, setIsLoading] = useState(false);
    /** @type {[{ blackListResponse?: any, documentResponses?: any }, Function]} */
    const [editBlackList, setEditBlackList] = useState(null);
    const [editMode, setEditMode] = useState(false);
    const [searchFetchLoading, setSearchFetchLoading] = useState(false);

    const [submitted, setSubmitted] = useState(false);
    const [isDrawerOpen, setIsDrawerOpen] = useState(false);
    const [isSubmitLoading, setIsSubmitLoading] = useState(false);
    const [isSearchLoading, setIsSearchLoading] = useState(false);
    const [searchSubmitLoading, setSearchSubmitLoading] = useState(false);
    const [searchRes, setSearchRes] = useState(false);
    const [fromDate, setFromDate] = useState(null);
    const [toDate, setToDate] = useState(null);
    const [action, setAction] = useState(null);

    // Dropdown states
    const [documentTypeOptions, setDocumentTypeOptions] = useState([]);
    const [fileNameOptions, setFileNameOptions] = useState([]);
    const [activeOptions, setActiveOptions] = useState([]);
    const [statusOptions, setStatusOptions] = useState([]);
    const [blkTypeOptions, setBlkTypeOptions] = useState([]);
    const [blkActionOptions, setBlkActionOptions] = useState([]);
    const [blkCategoryOptions, setBlkCategoryOptions] = useState([]);
    const [titleOptions, setTitleOptions] = useState([]);
    const [departmentOptions, setDepartmentOptions] = useState([]);
    const [typeOptions, setTypeOptions] = useState([]);
    const [reloadDates, setReloadDates] = useState(true);
    const [selectedRows, setSelectedRows] = useState([]);

    // Refs
    const docRef = useRef();
    const blkRef = useRef();
    const codeRef = useRef(null);
    const gridRef = useRef(null);

    const [searchFieldReset, setSearchFieldReset] = useState(false);
    const [files, setFiles] = useState([]);

    const [activeAccordion1, setActiveAccordion1] = useState(true);
    const [resetVisible, setResetVisible] = useState(false);
    const [searchClicked, setSearchClicked] = useState(false);

    useEffect(() => {
        (async () => {
            const {
                active: activeOpt,
                status: statusOpt,
                title: titleOpt,
                departments: departmentsOpt,
                contact: contactOpt,
                doc_type: docTypeOpt,
                "file_name#11": fileNameOpt,
                blk_type: blkTypeOpt,
                blk_action: blkActionOpt,
                blk_category: blkCategoryOpt,
            } = await getBulkDropdownData([
                { key: "active" },
                { key: "status" },
                { key: "title" },
                { key: "departments" },
                { key: "contact" },
                { key: "doc_type" },
                { key: "file_name", group: "11" },
                { key: "blk_type" },
                { key: "blk_action" },
                { key: "blk_category" },
            ]);

            setActiveOptions(activeOpt);
            setStatusOptions(statusOpt);
            setTitleOptions(titleOpt);
            setDepartmentOptions(departmentsOpt);
            setTypeOptions(contactOpt);
            setDocumentTypeOptions(docTypeOpt);
            setFileNameOptions(fileNameOpt);
            setBlkTypeOptions(blkTypeOpt);
            setBlkActionOptions(blkActionOpt);
            setBlkCategoryOptions(blkCategoryOpt);
        })();
    }, []);

    useEffect(() => {
        if (reloadDates) {
            const aWeekAgo = new Date();
            aWeekAgo.setDate(aWeekAgo.getDate() - 7);
            const formattedWeekAgo = aWeekAgo.toISOString().split("T")[0];
            const formattedToday = new Date().toISOString().split("T")[0];
            setFromDate(formattedWeekAgo);
            setToDate(formattedToday);
        }
        setReloadDates(false);
    }, [reloadDates]);

    const handleDrawerOpen = () => {
        dispatch(errorActions.clearBlackListError());
        setEditMode(false);
        setDrawerTab(firstViableTab);
        setEditBlackList(null);
        setIsDrawerOpen(true);
    };

    const reset = () => {
        blkRef.current?.resetForm();
        setEditMode(false);
        setEditBlackList(null);
    };

    const handleDrawerClose = () => {
        setIsDrawerOpen(false);
    };

    const handleReset = (e) => {
        e.preventDefault();
        if (tab === 1) {
            setSearchClicked(false);
            setResetVisible(false);
        }
    };

    const nextButtonHandler = () => {
        switch (drawerTab) {
            case 1: {
                const blackListErrors = blackListValidation(blkRef.current.getData());
                if (Object.keys(blackListErrors).length > 0) {
                    dispatch(
                        errorActions.addBlackListErrorObject({
                            form: "blackListErrors",
                            errors: blackListErrors,
                        })
                    );
                    toast.error("please fill the required fields", {
                        autoClose: 5000,
                        containerId: "Main",
                    });
                    return;
                } else {
                    dispatch(errorActions.clearBlackListError());
                }
                break;
            }
            default:
        }
        setDrawerTab(next());
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setIsSubmitLoading(true);

        dispatch(errorActions.clearBlackListError());

        const blackListErrors = blackListValidation(blkRef.current.getData());

        if (Object.keys(blackListErrors).length > 0) {
            dispatch(
                errorActions.validateBlackList({
                    blackListData: blkRef.current.getData(),
                })
            );
            setIsSubmitLoading(false);
            toast.error("There was an error adding Black List", {
                autoClose: 5000,
                containerId: "Main",
            });
            return;
        }

        if (editMode) {
            const response = await updateBlackList({
                document: docRef.current.getData(),
                blackListRequest: {
                    ...blkRef.current.getData(),
                    author: user?.username,
                },
                author: user?.username,
            });

            if (response?.status === 200) {
                const documents = docRef.current.getData();
                let hasUploadErrors = false;

                for (let doc of documents.created) {
                    const { file, ...rest } = doc;
                    const formData = new FormData();
                    formData.append("multipartFile", doc.file);
                    formData.append("documentRequest", JSON.stringify(rest));
                    formData.append("isImage", rest.docCategory === 1);
                    const res = await addAndUpload(formData);
                    if (res && res?.responseCode === "2040") {
                        toast.success(res?.message, {
                            autoClose: 5000,
                            containerId: "Main",
                        });
                    } else {
                        hasUploadErrors = true;
                        docRef.current.markUploadFailed(doc.id ?? doc.fileName);
                        toast.error(res?.message || `Error adding document ${doc.docName}`, {
                            autoClose: 5000,
                            containerId: "Main",
                        });
                    }
                }

                for (let doc of documents.modified) {
                    const { file, ...rest } = doc;
                    const formData = new FormData();
                    formData.append("multipartFile", doc.file);
                    formData.append("documentRequest", JSON.stringify(rest));
                    formData.append("isImage", rest.docCategory === 1);
                    const res = await updateAndUpload(formData);
                    if (res && res?.responseCode === "2040") {
                        toast.success(res?.message, {
                            autoClose: 5000,
                            containerId: "Main",
                        });
                    } else {
                        hasUploadErrors = true;
                        docRef.current.markUploadFailed(doc.id ?? doc.fileName);
                        toast.error(res?.message || `Error updating document ${doc.docName}`, {
                            autoClose: 5000,
                            containerId: "Main",
                        });
                    }
                }

                if (documents?.deleted?.length > 0) {
                    await deleteDocumentList({
                        docIdList: documents?.deleted,
                    });
                    toast.success("Documents deleted successfully!", {
                        autoClose: 5000,
                        containerId: "Main",
                    });
                }

                toast.success(response?.data?.message, {
                    autoClose: 5000,
                    containerId: "Main",
                });
                setIsSubmitLoading(false);
            } else {
                toast.error(response?.data?.message, {
                    autoClose: 5000,
                    containerId: "Main",
                });
                setIsSubmitLoading(false);
            }
            reset();
        } else {
            const response = await addBlackList({
                document: docRef.current.getData(),
                blackListRequest: blkRef.current.getData(),
                author: user?.username,
            });

            if (response?.data?.data?.blkId != null) {
                const documents = docRef.current.getData();
                let hasUploadErrors = false;

                for (let doc of documents) {
                    const { file, ...rest } = doc;
                    const formData = new FormData();
                    formData.append("multipartFile", doc.file);
                    formData.append(
                        "documentRequest",
                        JSON.stringify({
                            ...rest,
                            docReference: response.data.data,
                        })
                    );
                    formData.append("isImage", rest.docCategory === 1);
                    const res = await addAndUpload(formData);
                    if (res && res?.responseCode === "2040") {
                        toast.success(res?.message, {
                            autoClose: 5000,
                            containerId: "Main",
                        });
                    } else {
                        hasUploadErrors = true;
                        docRef.current.markUploadFailed(doc.id ?? doc.fileName);
                        toast.error(res?.message || `Error adding document ${doc.docName}`, {
                            autoClose: 5000,
                            containerId: "Main",
                        });
                    }
                }
                toast.success(response?.data?.message, {
                    autoClose: 5000,
                    containerId: "Main",
                });

                if (hasUploadErrors) {
                    setIsSubmitLoading(false);
                    return;
                }

                setIsSubmitLoading(false);
            } else if (response?.data?.data?.blkId === null) {
                toast.error(response?.data?.message, {
                    autoClose: 5000,
                    containerId: "Main",
                });
                setIsSubmitLoading(false);
            } else {
                toast.error("Error occurred adding Black List", {
                    autoClose: 5000,
                    containerId: "Main",
                });
                setIsSubmitLoading(false);
            }
            reset();
        }
        setIsSubmitLoading(false);
    };

    const handleSearchSubmit = async (e) => {
        e.preventDefault();
        if (tab === 1) {
            setActiveAccordion1(false);
            setResetVisible(true);
            setSearchClicked(true);
            setSearchSubmitLoading(true);
        }
        const response = await searchBlackList({
            blkCode: codeRef.current.value,
            blkCategory: blkCategory,
            blkReferance: vehEmp,
            blkType: blkType,
            blkAccident: "",
            blkDescription: "",
            blkFrom: fromDate,
            blkTo: toDate,
            blkStatus: status,
            blkActive: active,
            blkAction: action,
        });
        setSearchRes(response);
        setSearchSubmitLoading(false);
    };

    const handleSearchReset = (e) => {
        e.preventDefault();
        setSearchFieldReset(true);
        codeRef.current.value = "";
        setBlkCategory(1);
        setvehEmp("");
        setBlkType(null);
        setStatus(null);
        setActive(null);
        setReloadDates(true);
    };

    const handleSelectionChanged = useCallback(() => {
        if (gridRef.current) {
            const selectedData = gridRef.current.getSelectedData();
            setSelectedRows(selectedData);
        }
    }, []);

    // 1. Safely parse the JSON permissions string
    const parsedPermissions = useMemo(() => {
        if (!user?.permissions) return null;
        if (typeof user.permissions === "object") return user.permissions;
        try {
            return JSON.parse(user.permissions);
        } catch (err) {
            console.error("Failed to parse user permissions JSON:", err);
            return null;
        }
    }, [user?.permissions]);

    // 2. Check current action status
    const currentAction = editBlackList?.blackListResponse?.blkAction;
    const isRecordAlreadyUnblocked = currentAction === ACTION_KEYS.UNBLOCKED;

    // 3. Check if logged-in user is the creator
    const isCreator = Boolean(
        user?.username &&
        editBlackList?.blackListResponse?.createdBy &&
        user.username.trim().toLowerCase() ===
        editBlackList.blackListResponse.createdBy.trim().toLowerCase()
    );

    // 4. Check explicit unblock permission from your parsed permissions object
    const hasUnblockPermission = Boolean(
        parsedPermissions?.masterdata?.BLACKLIST?.BLACKLIST_UNBLOCK?.UPDATE === true ||
        parsedPermissions?.masterdata?.BLACKLIST?.BLACKLIST_UNBLOCK?.CREATE === true
    );

    // 5. Eligible to unblock if creator OR holding the unblock permission
    const canUnblock = isCreator || hasUnblockPermission;

    // 6. Disable condition
    const isUpdateDisabled = editMode ? isRecordAlreadyUnblocked || !canUnblock : false;

    return (
        <>
            <div
                className="ui attached tabular"
                style={{
                    display: "flex",
                    justifyContent: "space-around",
                }}
            >
                <div className="ui tabular menu attached">
                    <div
                        className={`item ${tab === 1 ? "active" : ""}`}
                        onClick={() => {
                            setTab(1);
                        }}
                    >
            <span>
              <XSquare size={15} style={{ marginRight: "5px" }} />
            </span>
                        BlackList Search
                    </div>
                </div>
                <div
                    style={{
                        display: "flex",
                        justifyContent: "flex-end",
                        marginTop: "0px",
                    }}
                    className="ui tabular menu top attached"
                >
                    <Button
                        template="drawerOpen"
                        disabled={firstViableTab === -1}
                        onClick={handleDrawerOpen}
                        authenticated={true}
                        form={FORMS.BLACKLIST.BLK.form}
                        module={MODULES.Master}
                        editMode={false}
                    />
                </div>
            </div>
            <div
                className="ui bottom attached active tab segment"
                style={{ display: tab === 1 ? "block" : "none" }}
            >
                <div className="ui styled accordion" style={{ width: "100%" }}>
                    <div
                        className={`title ${activeAccordion1 ? "active" : ""}`}
                        onClick={() => setActiveAccordion1(!activeAccordion1)}
                    >
                        <div
                            style={{
                                display: "flex",
                                justifyContent: "space-between",
                                alignItems: "center",
                            }}
                        >
                            <div>
                                <span>Search</span>
                                <Search size={14} style={{ marginLeft: "10px" }} />
                                <i className="dropdown icon"></i>
                            </div>

                            <i
                                className="refresh icon primary button"
                                style={{
                                    display: `${resetVisible ? "flex" : "none"}`,
                                    cursor: "pointer",
                                }}
                                onClick={(e) => {
                                    handleReset(e);
                                }}
                            ></i>
                        </div>
                    </div>
                    <div className={`content ${activeAccordion1 ? "active" : ""}`}>
                        {isSearchLoading ? (
                            <LoadingSpinner />
                        ) : (
                            <form className="ui form">
                                <div className="four fields">
                                    <div className="field">
                                        <label>Black List Code</label>
                                        <input
                                            type="text"
                                            placeholder="Black List Code"
                                            name="blackListCode"
                                            ref={codeRef}
                                        />
                                    </div>
                                    <div className="field">
                                        <label>Type</label>
                                        <Select
                                            placeholder="Type"
                                            options={blkTypeOptions}
                                            value={blkType}
                                            style={{ height: "20px" }}
                                            onChange={(e, data) => {
                                                setBlkType(data.value);
                                            }}
                                        />
                                    </div>
                                    <div className="field">
                                        <label>Category</label>
                                        <Select
                                            placeholder="Type"
                                            options={blkCategoryOptions}
                                            value={blkCategory}
                                            style={{ height: "20px" }}
                                            onChange={(e, data) => {
                                                setBlkCategory(data.value);
                                                setSearchFieldReset(true);
                                            }}
                                        />
                                    </div>

                                    <SearchDropdown
                                        key={1}
                                        editMode={false}
                                        reset={searchFieldReset}
                                        setReset={setSearchFieldReset}
                                        clearable={true}
                                        onClear={() => {
                                            setvehEmp("");
                                        }}
                                        onResultSelect={(data) => setvehEmp(data)}
                                        api={async ({
                                                        searchText,
                                                        type,
                                                        value,
                                                        param1 = "",
                                                        param2 = "2",
                                                    }) => {
                                            type === "vehicle" ? (value = searchText) : (value = value);
                                            return await getOtherRefSearchDropdown({
                                                searchText,
                                                type,
                                                value,
                                                param1,
                                                param2,
                                            });
                                        }}
                                        {...(blkCategory === 1
                                            ? {
                                                apiDependency: {
                                                    type: "vehicle",
                                                },
                                            }
                                            : {
                                                apiDependency: {
                                                    type: "employee",
                                                    value: "Driver",
                                                },
                                            })}
                                        label="Vehicle/Employee"
                                    />
                                </div>

                                <div className="four fields">
                                    <div className="field">
                                        <Input
                                            label="Start Date"
                                            type="date"
                                            name="empExpiaryDate"
                                            placeholder="To Date"
                                            max={toDate ?? new Date().toISOString().split("T")[0]}
                                            value={fromDate}
                                            onChange={(e) => {
                                                setFromDate(e.target.value);
                                            }}
                                            required={true}
                                        />
                                    </div>
                                    <div className="field">
                                        <Input
                                            label="End Date"
                                            type="date"
                                            name="empExpiaryDate"
                                            placeholder="To Date"
                                            min={fromDate}
                                            max={new Date().toISOString().split("T")[0]}
                                            value={toDate}
                                            disabled={!fromDate}
                                            onChange={(e) => {
                                                setToDate(e.target.value);
                                            }}
                                            required={true}
                                        />
                                    </div>
                                    <Dropdown
                                        placeholder="Action"
                                        options={blkActionOptions}
                                        value={action}
                                        field="blk_action"
                                        style={{ height: "20px" }}
                                        onChange={(e, data) => {
                                            setAction(data.value);
                                        }}
                                        label="Action"
                                    />
                                </div>

                                <div
                                    style={{
                                        width: "100%",
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "flex-end",
                                    }}
                                >
                                    <div className="field" style={{ width: "100%" }}>
                                        <Button
                                            template="search"
                                            onClick={(e) => {
                                                handleSearchSubmit(e);
                                            }}
                                        />
                                        <Button
                                            template="searchReset"
                                            onClick={(e) => {
                                                handleSearchReset(e);
                                            }}
                                        />
                                    </div>
                                </div>
                            </form>
                        )}
                    </div>
                </div>
                {searchClicked && (
                    <>
                        {searchSubmitLoading ? (
                            <LoadingSpinner />
                        ) : (
                            <Grid
                                ref={gridRef}
                                isLoading={searchFetchLoading}
                                content={searchRes}
                                cellSizeStrategy="fitCellContents"
                                heading={
                                    searchRes?.length > 0
                                        ? "Filtered Blacklisted Records List"
                                        : ""
                                }
                                multiRowSelection={true}
                                showDownloadButton={selectedRows && selectedRows?.length > 0}
                                onSelectionChanged={handleSelectionChanged}
                                downloadParam={JSON.stringify(selectedRows)}
                                downloadAction="Blacklisted records Excel Download"
                                reportName={"Blacklisted Records.csv"}
                                fitContentCellls={["blkAccident"]}
                                config={[
                                    {
                                        headerName: "Id",
                                        field: "blkId",
                                        sort: "desc",
                                        hide: true,
                                    },
                                    {
                                        headerName: "Code",
                                        field: "blkCode",
                                        headerCheckboxSelection: true,
                                        checkboxSelection: true,
                                    },
                                    {
                                        headerName: "Category",
                                        field: "blkCategory",
                                    },
                                    {
                                        headerName: "Vehicle/Employee",
                                        field: "blkReferenceName",
                                    },
                                    {
                                        headerName: "Type",
                                        field: "blkType",
                                    },
                                    {
                                        headerName: "Action",
                                        field: "blkAction",
                                    },
                                    {
                                        headerName: "Accident",
                                        field: "blkAccident",
                                        cellRenderer: ResizableScrollingTextRenderer,
                                    },
                                    {
                                        headerName: "Blacklist Start Date",
                                        cellRenderer: DateOnlyCellRenderer,
                                        field: "blkFrom",
                                    },
                                    {
                                        headerName: "Blacklist End Date",
                                        cellRenderer: DateOnlyCellRenderer,
                                        field: "blkTo",
                                    },
                                    {
                                        headerName: "Blacklist Duration",
                                        field: "blkDuration",
                                    },
                                    {
                                        headerName: "Transporter",
                                        cellRenderer: ResizableScrollingTextRenderer,
                                        field: "stkName",
                                    },
                                    {
                                        headerName: "Remark",
                                        cellRenderer: ResizableScrollingTextRenderer,
                                        field: "blkDescription",
                                    },
                                    {
                                        headerName: "Unblock By",
                                        field: "lastModifiedBy",
                                        cellRenderer: ResizableScrollingTextRenderer,
                                    },
                                    {
                                        headerName: "Unblock Date",
                                        cellRenderer: DateOnlyCellRenderer,
                                        field: "lastModifiedDate",
                                    },
                                    {
                                        headerName: "Created By",
                                        field: "createdBy",
                                        cellRenderer: ResizableScrollingTextRenderer,
                                    },
                                    {
                                        headerName: "Created Date",
                                        cellRenderer: DateOnlyCellRenderer,
                                        field: "createdDate",
                                    },
                                ]}
                                onCellDoubleClicked={async (props) => {
                                    setSearchFetchLoading(true);
                                    const response = await getBlackListById(props.data.blkId);
                                    setEditBlackList(response);
                                    setEditMode(true);
                                    setIsDrawerOpen(true);
                                    setSearchFetchLoading(false);
                                    setDrawerTab(firstViableTab);
                                }}
                            />
                        )}
                    </>
                )}
            </div>

            {/* Side Drawer */}
            <SideDrawer
                open={isDrawerOpen}
                handleDrawerClose={handleDrawerClose}
                head={
                    editMode ? (
                        <div
                            className="blacklist-code-header"
                            style={{ padding: "10px 16px", borderBottom: "1px solid #ccc" }}
                        >
                            <h3>
                                Black List Code: {editBlackList?.blackListResponse?.blkCode}
                            </h3>
                        </div>
                    ) : null
                }
                body={
                    <form className="drawer-content" onSubmit={handleSubmit}>
                        <div style={{ width: "60vw", boxShadow: "none" }}>
                            <div className="ui top attached tabular menu">
                                <Tab
                                    Icon={XSquare}
                                    onClick={() => {
                                        setDrawerTab(1);
                                    }}
                                    active={drawerTab === 1}
                                    errors={Object.keys(errorState.blackListErrors).length !== 0}
                                    label="BlackList"
                                    authenticated={true}
                                    form={FORMS.BLACKLIST.BLK_DETAIL.form}
                                    showMandatory={true}
                                    isMandatory={true}
                                />
                                <Tab
                                    Icon={Upload}
                                    onClick={() => {
                                        setDrawerTab(2);
                                    }}
                                    active={drawerTab === 2}
                                    label="Documents"
                                    authenticated={true}
                                    form={FORMS.BLACKLIST.BLK_DOCUMENT.form}
                                />
                            </div>

                            <div
                                className="ui bottom attached active tab segment"
                                style={{ display: drawerTab === 1 ? "block" : "none" }}
                            >
                                <BlackListForm
                                    errors={errorState.blackListErrors}
                                    ref={blkRef}
                                    isLoading={isLoading}
                                    user={user}
                                    canUnblock={canUnblock}
                                    isRecordAlreadyUnblocked={isRecordAlreadyUnblocked}
                                    dropdown={{
                                        blkTypeOptions,
                                        blkActionOptions,
                                        blkCategoryOptions,
                                        statusOptions,
                                        activeOptions,
                                    }}
                                    editMode={editMode}
                                    currentValues={
                                        editMode ? editBlackList?.blackListResponse : null
                                    }
                                />
                            </div>

                            <div
                                className="ui bottom attached active tab segment"
                                style={{ display: drawerTab === 2 ? "block" : "none" }}
                            >
                                <DocumentUpload
                                    opeType={11}
                                    ref={docRef}
                                    files={files}
                                    setFiles={setFiles}
                                    reference={editBlackList?.blackListResponse?.blkId ?? null}
                                    dropdown={{
                                        documentTypeOptions,
                                        statusOptions,
                                        fileNameOptions,
                                        activeOptions,
                                    }}
                                    isLoading={isLoading}
                                    editMode={editMode}
                                    currentValues={
                                        editMode ? editBlackList?.documentResponses : null
                                    }
                                />
                            </div>

                            <div
                                style={{
                                    width: "100%",
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "flex-end",
                                }}
                            >
                                <div style={{ display: "flex", alignItems: "center" }}>
                                    {drawerTab > firstViableTab && (
                                        <Button
                                            template="back"
                                            tab={drawerTab - 1}
                                            module={MODULES.Master}
                                            section={FORMS.BLACKLIST}
                                            authenticated={true}
                                            onClick={() => setDrawerTab(prev())}
                                        />
                                    )}
                                    {drawerTab < lastViableTab && (
                                        <Button
                                            template="next"
                                            tab={drawerTab + 1}
                                            module={MODULES.Master}
                                            section={FORMS.BLACKLIST}
                                            authenticated={true}
                                            onClick={nextButtonHandler}
                                        />
                                    )}
                                    {editMode ? (
                                        <Button
                                            type="submit"
                                            template="create"
                                            isLoading={isSubmitLoading}
                                            create={!editMode}
                                            form={FORMS.BLACKLIST.BLK.form}
                                            module={MODULES.Master}
                                            section={FORMS.BLACKLIST}
                                            authenticated={true}
                                            editMode={editMode}
                                            disabled={isSubmitLoading || isUpdateDisabled}
                                        />
                                    ) : drawerTab === lastViableTab && !isLoading ? (
                                        <Button
                                            type="submit"
                                            template="create"
                                            isLoading={isSubmitLoading}
                                            create={!editMode}
                                            form={FORMS.BLACKLIST.BLK.form}
                                            module={MODULES.Master}
                                            section={FORMS.BLACKLIST}
                                            authenticated={true}
                                            editMode={editMode}
                                        />
                                    ) : (
                                        ""
                                    )}
                                </div>
                            </div>
                            {submitted && errorState.hasErrors && (
                                <UIMessage type="negative" width="100%" centered={false}>
                                    <div className="content">
                                        <div className="header">
                                            There was some errors with your submission
                                        </div>
                                        {Object.keys(errorState.blackListErrors).length > 0 && (
                                            <p>
                                                <div className="header">Employee</div>
                                                <div className="content">
                                                    <ul>
                                                        {Object.entries(errorState.blackListErrors).map(
                                                            ([_, value]) => (
                                                                <li key={_}>{value}</li>
                                                            )
                                                        )}
                                                    </ul>
                                                </div>
                                            </p>
                                        )}
                                    </div>
                                </UIMessage>
                            )}
                        </div>
                    </form>
                }
                style={{ width: "60vw", boxShadow: "none" }}
            />
        </>
    );
};

export default BlackList;